import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { TransferTask, useTransferQueue } from "./app/transferQueue";
import { humanReadableSize } from "./app/utils";
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";

function TaskRow({ task }: { task: TransferTask }) {
  const percent =
    task.status === "completed"
      ? 100
      : task.total > 0
      ? Math.min(100, (task.loaded / task.total) * 100)
      : 0;

  return (
    <ListItem divider sx={{ display: "block", py: 1.25 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
          {task.name}
        </Typography>
        {task.status === "failed" ? (
          <Tooltip title={task.error?.message ?? "Failed"}>
            <ErrorOutlineIcon color="error" fontSize="small" />
          </Tooltip>
        ) : task.status === "completed" ? (
          <CheckCircleOutlineIcon color="success" fontSize="small" />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {Math.round(percent)}%
          </Typography>
        )}
      </Stack>

      <LinearProgress
        variant={
          task.status === "pending" && task.total === 0
            ? "indeterminate"
            : "determinate"
        }
        value={percent}
        color={
          task.status === "failed"
            ? "error"
            : task.status === "completed"
            ? "success"
            : "primary"
        }
        sx={{ height: 6, borderRadius: 3, my: 0.75 }}
      />

      <Typography variant="caption" color="text.secondary">
        {humanReadableSize(task.loaded)} / {humanReadableSize(task.total)}
        {task.status === "pending" && " · queued"}
      </Typography>
    </ListItem>
  );
}

function ProgressDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState(1);
  const transferQueue: TransferTask[] = useTransferQueue();

  const tasks = useMemo(() => {
    const taskType = tab === 0 ? "download" : "upload";
    return Object.values(transferQueue).filter(
      (task) => task.type === taskType
    );
  }, [tab, transferQueue]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Progress</DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, newTab) => setTab(newTab)}
        sx={{ "& .MuiTab-root": { flexBasis: "50%" } }}
      >
        <Tab label="Downloads" />
        <Tab label="Uploads" />
      </Tabs>
      {tasks.length === 0 ? (
        <DialogContent>
          <Typography textAlign="center" color="text.secondary">
            No tasks
          </Typography>
        </DialogContent>
      ) : (
        <DialogContent sx={{ padding: 0 }}>
          <Box>
            <List disablePadding>
              {tasks.map((task, index) => (
                <TaskRow key={`${task.remoteKey}-${index}`} task={task} />
              ))}
            </List>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
}

export default ProgressDialog;
